/**
 * useBrand Composable
 * 
 * A composable that manages brand-specific theming for the dashboard.
 * It detects the current user's brand and provides brand data including
 * logos, colors, and styling information.
 * 
 * Features:
 * - Automatic brand detection from authenticated user
 * - Role-based logo selection (admin vs regular user)
 * - Dynamic brand color management
 * - CSS variable injection for brand theming
 * - Admin customer view mode: overrides brand colors/logo with selected view brand
 * 
 * @returns {Object} An object containing brand data and theming methods
 */

import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { getBrandById, type Brand } from '@/config/brands'

export function useBrand() {
  const authStore = useAuthStore()

  /**
   * Get current brand based on logged-in user's brand_id.
   * When admin is in customer view mode, uses the selected view brand instead.
   */
  const currentBrand = computed<Brand | undefined>(() => {
    const user = authStore.currentUser
    if (!user) return undefined

    let brandId: number

    if (user.role === 'admin' && authStore.adminViewBrandId !== null) {
      // Admin in customer preview mode → use the previewed brand
      brandId = authStore.adminViewBrandId
    } else if (user.role === 'admin') {
      // Admin in their own panel → use admin's chosen brand preference
      brandId = authStore.adminBrandId
    } else {
      // Regular customer → use their own brand
      brandId = user.brand_id
    }

    return getBrandById(brandId)
  })

  /**
   * Get brand logo based on user role and view mode.
   * Admin in customer view sees the customer (dashboard) logo of the selected brand.
   */
  const brandLogo = computed<string | undefined>(() => {
    if (!currentBrand.value) return undefined

    const user = authStore.currentUser
    // Show admin logo only when admin is in their own admin mode
    const isAdminMode = user?.role === 'admin' && authStore.adminViewBrandId === null

    return isAdminMode
      ? currentBrand.value.adminLogo
      : currentBrand.value.dashboardLogo
  })

  /**
   * Get brand colors for theming.
   * Returns default colors if no brand is detected.
   */
  const brandColors = computed(() => {
    if (!currentBrand.value) {
      // Default colors when no brand is detected
      return {
        primary: '#4338CA',
        sidebarBg: '#4338CA',
        sidebarActiveBg: '#5b4fd4',
        accent: '#4338CA',
        hover: '#5b4fd4',
      }
    }

    return {
      primary: currentBrand.value.color,
      sidebarBg: currentBrand.value.sidebarBg,
      sidebarActiveBg: currentBrand.value.sidebarActiveBg,
      accent: currentBrand.value.accentColor,
      hover: currentBrand.value.hoverColor,
    }
  })

  /**
   * Apply brand colors and font as CSS variables to document root.
   * This allows components to use brand colors and font via CSS variables.
   */
  const applyBrandColors = () => {
    const root = document.documentElement
    const colors = brandColors.value

    root.style.setProperty('--brand-primary', colors.primary)
    root.style.setProperty('--brand-sidebar-bg', colors.sidebarBg)
    root.style.setProperty('--brand-sidebar-active-bg', colors.sidebarActiveBg)
    root.style.setProperty('--brand-accent', colors.accent)
    root.style.setProperty('--brand-hover', colors.hover)

    // Apply brand font if available
    if (currentBrand.value?.fontFamily) {
      root.style.setProperty('--brand-font-family', currentBrand.value.fontFamily)
    } else {
      root.style.setProperty('--brand-font-family', 'Poppins, sans-serif')
    }
  }

  return {
    currentBrand,
    brandLogo,
    brandColors,
    applyBrandColors,
  }
}
